import React, { useEffect, useMemo, useState } from 'react'
import { useSellerAuth } from '../contexts/SellerAuthContext'
import { useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Badge, Button, Dropdown, Layout, Menu, Modal, Space, Tag, Typography } from 'antd'
import type { MenuProps } from 'antd'
import {
  AppstoreOutlined,
  BellOutlined,
  BoxPlotOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import { consumeSellerNotice, peekSellerNotice, sellerNoticeEventName, type SellerUiNotice } from '../utils/sellerApi'

const { Header, Sider, Content } = Layout

type SellerMenuItem = {
  key: string
  icon?: React.ReactNode
  label: string
  requiredPermission?: string
  children?: Array<{
    key: string
    label: string
    requiredPermission?: string
  }>
}

type SellerMenuLeaf = {
  key: string
  label: string
  requiredPermission?: string
}

type SellerMenuConfigItem = {
  key: string
  icon?: React.ReactNode
  label: string
  requiredPermission?: string
  children?: SellerMenuLeaf[]
}

const fallbackPermissionLabel = (permission: string) =>
  permission
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const SellerLayout = ({ children }: { children: React.ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [upgradeNotice, setUpgradeNotice] = useState<SellerUiNotice | null>(null)
  const [permissionNotice, setPermissionNotice] = useState<SellerUiNotice | null>(null)
  const { seller, session, logout, isAuthenticated, isStaffSession, hasPermission } = useSellerAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const syncUiNotice = () => {
      const nextNotice = peekSellerNotice()

      if (!nextNotice) {
        return
      }

      if (nextNotice?.context === 'upgrade') {
        setUpgradeNotice(consumeSellerNotice())
        return
      }

      if (nextNotice?.context === 'permission') {
        setPermissionNotice(consumeSellerNotice())
      }
    }

    syncUiNotice()
    window.addEventListener(sellerNoticeEventName, syncUiNotice)

    return () => {
      window.removeEventListener(sellerNoticeEventName, syncUiNotice)
    }
  }, [])

  const menuConfig = useMemo(
    (): SellerMenuConfigItem[] => [
      { key: '/seller/dashboard', icon: <DashboardOutlined />, label: 'Dashboard', requiredPermission: 'dashboard.read' },
      { key: '/seller/products', icon: <AppstoreOutlined />, label: 'Products', requiredPermission: 'products.read' },
      { key: '/seller/inventory', icon: <BoxPlotOutlined />, label: 'Inventory', requiredPermission: 'inventory.read' },
      { key: '/seller/orders', icon: <ShoppingCartOutlined />, label: 'Orders', requiredPermission: 'orders.read' },
      { key: '/seller/heatmap', icon: <EnvironmentOutlined />, label: 'Heatmap', requiredPermission: 'orders.heatmap.view' },
      {
        key: 'team',
        icon: <UsergroupAddOutlined />,
        label: 'Team',
        children: [
          { key: '/seller/roles', label: 'Roles', requiredPermission: 'roles.read' },
          { key: '/seller/users', label: 'Users', requiredPermission: 'users.read' },
        ],
      },
      { key: '/seller/support', icon: <CustomerServiceOutlined />, label: 'Support', requiredPermission: 'support.read' },
      { key: '/seller/profile', icon: <UserOutlined />, label: 'Profile', requiredPermission: 'profile.read' },
      { key: '/seller/wallet', icon: <CreditCardOutlined />, label: 'Wallet', requiredPermission: 'wallet.read' },
      { key: '/seller/settings', icon: <SettingOutlined />, label: 'Settings', requiredPermission: 'settings.read' },
    ],
    [],
  )

  const visibleMenuConfig = useMemo(
    () =>
      menuConfig
        .map((item) => {
          if (item.children?.length) {
            const visibleChildren = item.children.filter(
              (child) => !child.requiredPermission || hasPermission(child.requiredPermission),
            )

            if (visibleChildren.length === 0) {
              return null
            }

            return {
              ...item,
              children: visibleChildren,
            }
          }

          if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
            return null
          }

          return item
        })
        .filter((item): item is SellerMenuConfigItem => item !== null),
    [menuConfig, hasPermission],
  )

  const menuItems = useMemo<MenuProps['items']>(
    () =>
      visibleMenuConfig.map((item) => {
        if (item.children?.length) {
          return {
            key: item.key,
            icon: item.icon,
            label: item.label,
            children: item.children.map((child) => ({
              key: child.key,
              label: child.label,
            })),
          }
        }

        return {
          key: item.key,
          icon: item.icon,
          label: item.label,
        }
      }),
    [visibleMenuConfig],
  )

  const flatMenuItems = useMemo(
    (): SellerMenuItem[] =>
      visibleMenuConfig.flatMap((item) =>
        item.children?.length
          ? item.children.map((child) => ({
              key: child.key,
              label: child.label,
              requiredPermission: child.requiredPermission,
            }))
          : [
              {
                key: item.key,
                label: item.label,
                requiredPermission: item.requiredPermission,
              },
            ],
      ),
    [visibleMenuConfig],
  )

  const firstAccessibleRoute = flatMenuItems[0]?.key ?? '/seller/dashboard'

  const selectedKey =
    flatMenuItems.find((item) => location.pathname.startsWith(item.key))?.key || firstAccessibleRoute

  const currentTitle =
    flatMenuItems.find((item) => item.key === selectedKey)?.label || 'Dashboard'

  const userMenu = useMemo(
    () => [
      ...(hasPermission('profile.read')
        ? [
            {
              key: 'profile',
              icon: <UserOutlined />,
              label: 'Profile',
              onClick: () => navigate('/seller/profile'),
            },
          ]
        : []),
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        danger: true,
        onClick: logout,
      },
    ],
    [hasPermission, logout, navigate],
  )

  const handleContinueWithCurrentPlan = () => {
    setUpgradeNotice(null)

    if (location.pathname !== '/seller/dashboard') {
      navigate('/seller/dashboard')
    }
  }

  const handleUpgradePlan = () => {
    setUpgradeNotice(null)
    navigate('/seller/settings')
  }

  const isMobileNavOpen = isMobile && !collapsed

  if (!isAuthenticated) {
    return <div className="seller-loading-screen">Preparing seller workspace...</div>
  }

  return (
    <>
      <Layout className={`seller-layout${isMobileNavOpen ? ' seller-layout--nav-open' : ''}`}>
        <Sider
          collapsible
          collapsed={collapsed}
          collapsedWidth={isMobile ? 0 : 82}
          width={260}
          breakpoint="lg"
          trigger={null}
          onBreakpoint={(broken) => {
            setIsMobile(broken)
            setCollapsed(broken)
          }}
          onCollapse={setCollapsed}
          className="seller-sider"
        >
          <div className="seller-sider-logo">
            <strong>{collapsed ? 'TS' : 'Trendify Seller'}</strong>
            {!collapsed ? <span>Commerce workspace</span> : null}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={['team']}
            items={menuItems}
            onClick={({ key }) => {
              if (typeof key === 'string' && key.startsWith('/seller/')) {
                const clickedItem = flatMenuItems.find((item) => item.key === key)

                if (clickedItem?.requiredPermission && !hasPermission(clickedItem.requiredPermission)) {
                  setPermissionNotice({
                    type: 'warning',
                    title: 'Not permitted',
                    description: 'You do not have permission to access this section. Ask your seller administrator to update your role permissions.',
                    context: 'permission',
                    requiredPermission: clickedItem.requiredPermission,
                    requiredPermissionLabel: fallbackPermissionLabel(clickedItem.requiredPermission),
                  })
                  return
                }

                navigate(key)

                if (isMobile) {
                  setCollapsed(true)
                }
              }
            }}
            style={{ marginTop: 18, paddingInline: 10 }}
          />
        </Sider>

        {isMobileNavOpen ? (
          <button
            type="button"
            className="seller-nav-backdrop"
            aria-label="Close seller navigation"
            onClick={() => setCollapsed(true)}
          />
        ) : null}

        <Layout
          className="seller-main-layout"
          style={{ marginInlineStart: isMobile ? 0 : collapsed ? 82 : 260 }}
        >
          <Header className="seller-topbar">
            <div className="seller-topbar-left">
              <Button
                type="text"
                className="seller-menu-toggle"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((value) => !value)}
                aria-label={collapsed ? 'Open seller navigation' : 'Collapse seller navigation'}
              />
              <div>
                <h1 className="seller-page-title">{currentTitle}</h1>
                <p className="seller-page-subtitle">Manage catalog, fulfillment, team, billing, and support.</p>
              </div>
            </div>
            <div className="seller-topbar-actions">
              <Badge count={0}>
                <BellOutlined style={{ fontSize: 20 }} />
              </Badge>
              <Dropdown menu={{ items: userMenu }} trigger={['click']}>
                <div className="seller-profile-trigger">
                  <Avatar style={{ backgroundColor: '#0f766e' }}>{seller?.business_name?.charAt(0) ?? 'S'}</Avatar>
                  <span>{session?.staff?.name ?? seller?.business_name}</span>
                </div>
              </Dropdown>
            </div>
          </Header>
          <Content className="seller-content">
            <div className="seller-content-inner">{children}</div>
          </Content>
        </Layout>
      </Layout>

      <Modal
        open={Boolean(upgradeNotice)}
        title={upgradeNotice?.title || 'Upgrade suggestion'}
        onCancel={handleContinueWithCurrentPlan}
        footer={[
          <Button key="continue" onClick={handleContinueWithCurrentPlan}>
            Continue with current features
          </Button>,
          <Button key="upgrade" type="primary" onClick={handleUpgradePlan}>
            Upgrade plan
          </Button>,
        ]}
        destroyOnHidden
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {upgradeNotice?.description}
          </Typography.Paragraph>

          {upgradeNotice?.currentPlanName ? (
            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                Current plan: {upgradeNotice.currentPlanName}
              </Typography.Title>
              <Typography.Text type="secondary">
                You can continue working with the features already included in your plan, or upgrade to unlock more access.
              </Typography.Text>
            </div>
          ) : null}

          {upgradeNotice?.requiredFeatureLabel ? (
            <Tag color="gold">Requested feature: {upgradeNotice.requiredFeatureLabel}</Tag>
          ) : null}

          {upgradeNotice?.currentPlanFeatures?.length ? (
            <div>
              <Typography.Text strong>Included in your current plan</Typography.Text>
              <div style={{ marginTop: 10 }}>
                {upgradeNotice.currentPlanFeatures.map((feature) => (
                  <Tag key={feature} color="blue" style={{ marginBottom: 8 }}>
                    {feature}
                  </Tag>
                ))}
              </div>
            </div>
          ) : null}
        </Space>
      </Modal>

      <Modal
        open={Boolean(permissionNotice)}
        title={permissionNotice?.title || 'Not permitted'}
        onCancel={() => setPermissionNotice(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setPermissionNotice(null)}>
            Close
          </Button>,
        ]}
        destroyOnHidden
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {permissionNotice?.description}
          </Typography.Paragraph>

          {permissionNotice?.requiredPermissionLabel ? (
            <Tag color="red">Required permission: {permissionNotice.requiredPermissionLabel}</Tag>
          ) : null}

          {isStaffSession && session?.staff ? (
            <Typography.Text type="secondary">
              Signed in as staff: {session.staff.name} ({session.staff.email})
            </Typography.Text>
          ) : null}
        </Space>
      </Modal>
    </>
  )
}

export default SellerLayout


